import { useEffect, useRef } from 'react';

const areas = [
  {
    name: 'South Lake Tahoe',
    tagline: 'Lakefront & Mountain Living',
    desc: 'Lakefront estates, mountain retreats, and a vibrant year-round luxury lifestyle in the heart of the Lake Tahoe basin.',
    icon: 'ri-water-flash-line',
  },
  {
    name: 'North Lake Tahoe',
    tagline: 'Incline Village & Tahoe City',
    desc: "Upscale communities with exceptional lake views, ski access, and some of the region's most coveted luxury properties.",
    icon: 'ri-mountain-line',
  },
  {
    name: 'Alpine County',
    tagline: 'Rare Alpine Estates',
    desc: "One of California's least populated counties — extraordinary privacy, breathtaking scenery, and legacy investment value.",
    icon: 'ri-landscape-line',
  },
  {
    name: 'El Dorado County',
    tagline: 'Mountain Communities',
    desc: 'Diverse properties from resort-adjacent luxury to rural acreage estates with easy highway access to the Bay Area.',
    icon: 'ri-home-4-line',
  },
  {
    name: 'Sierra Nevada',
    tagline: 'Mountain Corridor',
    desc: 'Resort property dynamics and seasonal demand expertise throughout the broader Sierra Nevada alpine range.',
    icon: 'ri-compass-3-line',
  },
];

export default function ServiceAreas() {
  const areaCardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = areaCardsRef.current?.querySelectorAll('.area-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.05 }
    );
    cards.forEach((card, i) => {
      const el = card as HTMLElement;
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      el.style.transition = `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.09}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.09}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 py-28 px-4 md:px-8 rounded-t-[2.5rem] -mt-8 overflow-hidden" style={{ background: '#F5F1EA' }}>
      {/* Giant decorative watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span className="font-serif text-[12rem] md:text-[18rem] text-charcoal/[0.022] font-light select-none whitespace-nowrap">
          Tahoe
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">Where We Work</p>
          <h2 className="font-serif text-5xl md:text-7xl text-charcoal font-light">
            Service <em className="italic">Areas</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/40" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/60" />
            <div className="w-16 h-px bg-gold/40" />
          </div>
          <p className="text-stone text-sm max-w-xl mx-auto leading-relaxed mt-6">
            Deep understanding of resort property dynamics, seasonal demand, and long-term value trends across the greater Lake Tahoe and Sierra Nevada region.
          </p>
        </div>

        {/* Area cards */}
        <div ref={areaCardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-14">
          {areas.map((area) => (
            <div
              key={area.name}
              className="area-card group bg-white rounded-2xl p-6 cursor-pointer hover:translate-y-[-3px] transition-all duration-400"
              style={{
                boxShadow: '0 2px 14px rgba(42,42,42,0.06)',
                border: '1px solid rgba(240,235,224,0.9)',
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold/10 border border-gold/15 mb-4 group-hover:bg-gold/20 transition-all duration-300">
                <i className={`${area.icon} text-gold text-base`} />
              </div>
              <p className="text-[9px] tracking-[0.3em] uppercase text-gold mb-1">{area.tagline}</p>
              <h3 className="font-serif text-lg text-charcoal font-medium mb-2">{area.name}</h3>
              <p className="text-stone text-xs leading-relaxed">{area.desc}</p>
              <div className="mt-4 w-6 h-px bg-gold/30 group-hover:w-12 transition-all duration-400" />
            </div>
          ))}
        </div>

        {/* Map — enlarged and prominent */}
        <div className="rounded-2xl overflow-hidden" style={{ boxShadow: '0 12px 60px rgba(42,42,42,0.12)', border: '1px solid rgba(196,169,98,0.12)' }}>
          <div className="h-80 md:h-[32rem]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d412534.8612849636!2d-120.4155879!3d38.9280063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809967d8f6cc1e59%3A0x2e70a2be55ecd96a!2sLake%20Tahoe%2C%20CA!5e0!3m2!1sen!2sus!4v1711000000001!5m2!1sen!2sus"
              className="w-full h-full"
              title="Lake Tahoe and Sierra Nevada Area Map — Cam Kirkwood Real Estate"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Location pills */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {['South Lake Tahoe', 'North Lake Tahoe', 'Alpine County', 'El Dorado County', 'Sierra Nevada'].map((loc) => (
            <span key={loc} className="flex items-center gap-1.5 text-xs text-stone">
              <i className="ri-map-pin-line text-gold" />
              {loc}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
